// context/AuthContext.js
import { useContext, createContext, useEffect, useState, useRef } from 'react';
import firebase, { auth } from '../firebase/firebaseConfig';
import {
  saveAuthToCookies,
  getAuthFromCookies,
  clearAuthCookies,
  isAuthenticated,
  checkCookieIntegrity
} from '../utils/auth-cookie-manager';
import { initializeUserInDatabase, getUserRequestStatus } from '../utils/api-helpers';

const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [sessionTerminated, setSessionTerminated] = useState(false);
  const [userRequestData, setUserRequestData] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const cookieCheckInterval = useRef(null);

  // Add this to your AuthContext component
  useEffect(() => {
    // Listen for auth errors globally
    if (window.firebase && window.firebase.auth) {
      window.firebase.auth().getRedirectResult()
        .then((result) => {
          if (result.credential) {
            console.log("Redirect successful:", result);
          }
        })
        .catch((error) => {
          console.log("Redirect error:", error);
          if (error.code === 'auth/redirect-cancelled-by-user') {
            console.log("User cancelled the redirect sign-in");
          }
        });
    }
  }, []);

  // Function to handle user initialization
  const handleUserInitialization = async (currentUser) => {
    if (currentUser) {
      try {
        const userData = await initializeUserInDatabase();
        if (userData) {
          setUserRequestData(userData);

          if (userData.isNewUser) {
            console.log(`Welcome! You have ${process.env.REACT_APP_FREE_REQ || 3} free requests to try our service.`);
          } else if (userData.freeRequestsRemaining > 0 && !userData.hasSubscription) {
            console.log(`You have ${userData.freeRequestsRemaining} free request${userData.freeRequestsRemaining > 1 ? 's' : ''} remaining.`);
          }
        }
      } catch (error) {
        console.error('Error initializing user:', error);
      }
    }
  };

  // context/AuthContext.js
  const googleSignIn = async () => {
    if (isAuthenticating) {
      console.log('Authentication already in progress');
      return { pending: true };
    }

    if (!auth) {
      const error = new Error("Firebase Authentication is not initialized");
      setAuthError(error);
      throw error;
    }

    try {
      setIsAuthenticating(true);
      setLoading(true);
      setSessionTerminated(false);
      setAuthError(null);

      const provider = new firebase.auth.GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');

      // IMPORTANT: Remove or modify custom parameters that might cause issues
      provider.setCustomParameters({
        prompt: 'select_account',
        // Add these to help with the popup issue
        access_type: 'offline',
        include_granted_scopes: 'true'
      });

      console.log("Starting Google authentication...");

      // Add a small delay to ensure UI is ready
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        // Try popup with better error handling
        const result = await auth.signInWithPopup(provider);

        if (result && result.user) {
          console.log("Popup sign-in successful!", result.user);

          if (typeof saveAuthToCookies === 'function') {
            saveAuthToCookies(result.user);
          }

          await handleUserInitialization(result.user);
          setUser(result.user);
          return result;
        }
      } catch (popupError) {
        console.log("Popup error details:", {
          code: popupError.code,
          message: popupError.message,
          email: popupError.email,
          credential: popupError.credential
        });

        // For popup-closed-by-user, try redirect immediately
        if (popupError.code === 'auth/popup-closed-by-user' ||
          popupError.code === 'auth/popup-blocked' ||
          popupError.code === 'auth/cancelled-popup-request') {

          console.log("Switching to redirect method...");

          // Use redirect as fallback
          await auth.signInWithRedirect(provider);
          sessionStorage.setItem('authRedirectPending', 'true');
          return { pending: true, message: 'Redirecting to Google...' };
        } else {
          throw popupError;
        }
      }

    } catch (error) {
      console.error("Google Sign-in Error:", error);
      setAuthError(error.message || "Authentication failed");
      throw error;
    } finally {
      setIsAuthenticating(false);
      if (!sessionStorage.getItem('authRedirectPending')) {
        setLoading(false);
      }
    }
  };

  // Alternative popup sign-in method (only if explicitly requested)
  const googleSignInWithPopup = async () => {
    if (isAuthenticating) {
      console.log('Authentication already in progress');
      return;
    }

    if (!auth) {
      const error = new Error("Firebase Authentication is not initialized");
      setAuthError(error);
      throw error;
    }

    try {
      setIsAuthenticating(true);
      setLoading(true);
      setAuthError(null);

      const provider = new firebase.auth.GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      // Wrap in a try-catch specifically for popup
      let result;
      try {
        result = await auth.signInWithPopup(provider);
      } catch (popupError) {
        // If popup fails, immediately fallback to redirect
        console.log("Popup failed, using redirect instead");
        await auth.signInWithRedirect(provider);
        sessionStorage.setItem('authRedirectPending', 'true');
        return { pending: true, message: 'Redirecting to Google...' };
      }

      if (result && result.user) {
        console.log("Popup sign-in successful!", result.user);

        if (typeof saveAuthToCookies === 'function') {
          saveAuthToCookies(result.user);
        }

        await handleUserInitialization(result.user);
        return result;
      }
    } catch (error) {
      console.error("Google Sign-in Error:", error);
      setAuthError(error);
      throw error;
    } finally {
      setLoading(false);
      setIsAuthenticating(false);
    }
  };

  const logOut = async () => {
    if (!auth) {
      const error = new Error("Firebase Authentication is not initialized");
      setAuthError(error);
      throw error;
    }

    try {
      setLoading(true);
      await auth.signOut();
      if (typeof clearAuthCookies === 'function') {
        clearAuthCookies();
      }
      setUser(null);
      setUserRequestData(null);
      localStorage.removeItem('userRequestData');
      sessionStorage.removeItem('authRedirectPending');
    } catch (error) {
      console.error("Log Out Error:", error);
      setAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Handle redirect result
  useEffect(() => {
    const handleRedirectResult = async () => {
      if (!auth) return;

      // Check if we're expecting a redirect result
      const redirectPending = sessionStorage.getItem('authRedirectPending');

      try {
        const result = await auth.getRedirectResult();

        if (result && result.user) {
          console.log("Redirect sign-in successful!", result.user);

          // Clear the pending flag
          sessionStorage.removeItem('authRedirectPending');

          if (typeof saveAuthToCookies === 'function') {
            saveAuthToCookies(result.user);
          }

          await handleUserInitialization(result.user);
          setUser(result.user);
          setLoading(false);
        } else if (redirectPending) {
          // We were expecting a redirect but didn't get a user
          console.log("Redirect completed but no user returned");
          sessionStorage.removeItem('authRedirectPending');
          setLoading(false);
        }
      } catch (error) {
        // Only log actual errors
        if (error.code && error.code !== 'auth/no-auth-event') {
          console.error("Redirect result error:", error);
          setAuthError(error);
          sessionStorage.removeItem('authRedirectPending');
        }
        setLoading(false);
      }
    };

    handleRedirectResult();
  }, []);

  // Fetch user request status periodically
  useEffect(() => {
    if (!user) return;

    const fetchStatus = async () => {
      try {
        const status = await getUserRequestStatus();
        if (status) {
          setUserRequestData(status);
        }
      } catch (error) {
        console.error('Error fetching user status:', error);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  // Set up Firebase auth state listener
  useEffect(() => {
    if (!auth) {
      console.warn("Firebase Authentication is not initialized.");
      setLoading(false);
      return;
    }

    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      console.log('Auth State Changed:', currentUser ? currentUser.email : 'No user');

      if (currentUser) {
        if (typeof saveAuthToCookies === 'function') {
          saveAuthToCookies(currentUser);
        }
        setSessionTerminated(false);

        await handleUserInitialization(currentUser);
      } else {
        if (typeof clearAuthCookies === 'function') {
          clearAuthCookies();
        }
        setUserRequestData(null);
        localStorage.removeItem('userRequestData');
      }

      setUser(currentUser);

      // Only set loading to false if we're not expecting a redirect
      const redirectPending = sessionStorage.getItem('authRedirectPending');
      if (!redirectPending) {
        setLoading(false);
      }
    }, (error) => {
      console.error("Auth State Change Error:", error);
      setAuthError(error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{
      googleSignIn,
      googleSignInWithPopup, // Export this as an alternative
      logOut,
      user,
      loading,
      authError,
      sessionTerminated,
      userRequestData,
      isAuthenticating,
      isAuthenticated: () => typeof isAuthenticated === 'function' ? isAuthenticated() : false
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const UserAuth = () => {
  return useContext(AuthContext);
};